/**
 * userUploadStore — learner media upload slice (Cloudinary via backend).
 *
 *   POST /api/uploads/image?folder=…   public image, raw binary body with the
 *                                      file's Content-Type + x-filename header
 *   POST /api/uploads/document         private doc (≤50MB), raw binary body
 *   DELETE /api/uploads                { publicId, cloudType, deliveryType }
 *
 * NOTE: these endpoints live at /api/uploads/* (NOT /api/learners/*) but are
 * used by the learner when attaching a file to an assignment, practical or
 * report submission (the submission body carries fileUrl/filePublicId/…).
 */
import { API_URL, resolveToken } from "./userUtils";
import { toDisplayString, safeFetch, explainApiError } from "../../utils/toDisplayString";

export const USER_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const USER_DOCUMENT_MAX_BYTES = 50 * 1024 * 1024;

export const userUploadInitialState = {
  userImageUploadLoading: false,
  userImageUploadError: null,
  userLastImageUpload: null,

  userDocumentUploadLoading: false,
  userDocumentUploadError: null,
  userLastDocumentUpload: null,

  userUploadDeleteLoading: false,
  userUploadDeleteError: null,
};

/** POST a raw binary body; returns the unwrapped payload or throws. */
async function uploadBinary(get, path, file, filename) {
  if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
  const token = resolveToken(get);
  const headers = {};
  if (file?.type) headers["Content-Type"] = file.type;
  if (filename) headers["x-filename"] = filename;
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await safeFetch(`${API_URL.replace(/\/$/, "")}${path}`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: file,
  });

  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      if (text.trim().startsWith("<")) {
        throw new Error(`Upload API returned HTML (${response.status}).`);
      }
      throw new Error(`Upload API returned a non-JSON response (${response.status}).`);
    }
  }
  if (!response.ok) {
    const err = new Error(
      explainApiError(response.status, data || {}, `Upload failed with status ${response.status}.`)
    );
    err.status = response.status;
    err.payload = data;
    throw err;
  }
  return data?.data && typeof data.data === "object" && !Array.isArray(data.data) ? data.data : data;
}

export function createUserUploadActions(set, get) {
  return {
    // POST /api/uploads/image?folder=… — file: image/* (≤10MB)
    uploadUserImage: async (file, folder) => {
      set({ userImageUploadLoading: true, userImageUploadError: null });
      try {
        if (!file) throw new Error("No file selected.");
        if (file.size > USER_IMAGE_MAX_BYTES) {
          throw new Error("Image is too large. Maximum size is 10MB.");
        }
        const path = folder
          ? `/api/uploads/image?folder=${encodeURIComponent(folder)}`
          : "/api/uploads/image";
        const result = await uploadBinary(get, path, file, file.name);
        set({ userImageUploadLoading: false, userLastImageUpload: result });
        return { success: true, upload: result };
      } catch (error) {
        const msg = toDisplayString(error.message, "Upload failed");
        set({ userImageUploadLoading: false, userImageUploadError: msg });
        return { success: false, error: msg, status: error.status };
      }
    },

    // POST /api/uploads/document — file: any (≤50MB)
    uploadUserDocument: async (file) => {
      set({ userDocumentUploadLoading: true, userDocumentUploadError: null });
      try {
        if (!file) throw new Error("No file selected.");
        if (file.size > USER_DOCUMENT_MAX_BYTES) {
          throw new Error("Document is too large. Maximum size is 50MB.");
        }
        const result = await uploadBinary(get, "/api/uploads/document", file, file.name);
        set({ userDocumentUploadLoading: false, userLastDocumentUpload: result });
        return { success: true, upload: result };
      } catch (error) {
        const msg = toDisplayString(error.message, "Upload failed");
        set({ userDocumentUploadLoading: false, userDocumentUploadError: msg });
        return { success: false, error: msg, status: error.status };
      }
    },

    // DELETE /api/uploads { publicId, cloudType, deliveryType }
    deleteUserUpload: async ({ publicId, cloudType, deliveryType } = {}) => {
      set({ userUploadDeleteLoading: true, userUploadDeleteError: null });
      try {
        if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
        const token = resolveToken(get);
        const headers = { Accept: "application/json", "Content-Type": "application/json" };
        if (token) headers.Authorization = `Bearer ${token}`;
        const response = await safeFetch(`${API_URL.replace(/\/$/, "")}/api/uploads`, {
          method: "DELETE",
          headers,
          credentials: "omit",
          body: JSON.stringify({ publicId, cloudType, deliveryType }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            explainApiError(response.status, data || {}, `Delete failed with status ${response.status}.`)
          );
        }
        set({ userUploadDeleteLoading: false });
        return { success: true, data: data?.data ?? data };
      } catch (error) {
        const msg = toDisplayString(error.message, "Failed to delete upload");
        set({ userUploadDeleteLoading: false, userUploadDeleteError: msg });
        return { success: false, error: msg, status: error.status };
      }
    },
  };
}

const userUploadActions = createUserUploadActions;
export default userUploadActions;
